type NullableText = string | null | undefined

interface WorkerCompanySource {
  companyName?: NullableText
  company?: NullableText
  orgName?: NullableText
}

interface WorkerRoleSource {
  jobRole?: NullableText
  position?: NullableText
  roleInPermit?: NullableText
}

interface WorkerIdentitySource {
  workerId?: NullableText
  cardNumber?: NullableText
  username?: NullableText
  fullName?: NullableText
}

const normalizeText = (value?: NullableText) => {
  if (typeof value !== "string") {
    return null
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

const normalizeIdentity = (value?: NullableText) => normalizeText(value)?.toLowerCase() ?? null

const normalizeComparisonValue = (value?: NullableText) =>
  normalizeText(value)
    ?.replace(/\s+/g, " ")
    .toLowerCase() ?? null

export const resolveWorkerCompanyName = (worker: WorkerCompanySource) => {
  const companyName = normalizeText(worker.companyName)
  const company = normalizeText(worker.company)
  const orgName = normalizeText(worker.orgName)

  const normalizedCompanyName = normalizeComparisonValue(companyName)
  const normalizedCompany = normalizeComparisonValue(company)
  const normalizedOrgName = normalizeComparisonValue(orgName)

  // Employee records from the contractor worker lookup can mirror org/unit into companyName
  // while the actual legal company is returned in `company`.
  if (
    company &&
    normalizedCompanyName &&
    normalizedOrgName &&
    normalizedCompanyName === normalizedOrgName &&
    normalizedCompany !== normalizedCompanyName
  ) {
    return company
  }

  return companyName ?? company ?? orgName ?? null
}

export const resolveWorkerJobRole = (worker: WorkerRoleSource) =>
  normalizeText(worker.jobRole) ??
  normalizeText(worker.position) ??
  normalizeText(worker.roleInPermit) ??
  ""

export const isSameWorkerDirectoryRecord = (
  worker: WorkerIdentitySource,
  candidate: WorkerIdentitySource,
) => {
  const workerId = normalizeIdentity(worker.workerId)
  const candidateWorkerId = normalizeIdentity(candidate.workerId)
  if (workerId && candidateWorkerId && workerId === candidateWorkerId) {
    return true
  }

  const cardNumber = normalizeIdentity(worker.cardNumber)
  const candidateCardNumber = normalizeIdentity(candidate.cardNumber)
  if (cardNumber && candidateCardNumber && cardNumber === candidateCardNumber) {
    return true
  }

  const username = normalizeIdentity(worker.username)
  const candidateUsername = normalizeIdentity(candidate.username)
  if (username && candidateUsername && username === candidateUsername) {
    return true
  }

  const fullName = normalizeIdentity(worker.fullName)
  const candidateFullName = normalizeIdentity(candidate.fullName)
  return Boolean(fullName && candidateFullName && fullName === candidateFullName)
}
